import * as React from 'react';

import { cn } from '~/util';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, type, ...props }, ref) => {
        return (
            <input
                type={type}
                className={cn(
                    ' rounded-md bg-background-light p-4 text-lg ring-offset-background hover:bg-background-semilight transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-secondary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    className,
                )}
                ref={ref}
                {...props}
            />
        );
    },
);
Input.displayName = 'Input';

export type NumberInputProps = Omit<
    InputProps,
    'type' | 'onChange' | 'value'
> & {
    onChange?: (value: number | null) => void;
    value?: number | null;
    min?: number;
    max?: number;
    padZeroStart?: number;
    decimalPlaces?: number;
};

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
    (
        {
            className,
            value,
            min,
            max,
            padZeroStart = 0,
            decimalPlaces,
            inputMode,
            onBlur,
            onFocus,
            onChange,
            ...props
        },
        ref,
    ) => {
        const [focusedValue, setFocusedValue] = React.useState<string | null>(
            null,
        );
        const displayValue =
            value == null
                ? ''
                : decimalPlaces == null
                  ? value.toString().padStart(padZeroStart, '0')
                  : value.toFixed(decimalPlaces);

        return (
            <Input
                type="text"
                inputMode={
                    inputMode ?? (decimalPlaces == null ? 'numeric' : 'decimal')
                }
                value={focusedValue ?? displayValue}
                className={className}
                onFocus={(e) => {
                    if (decimalPlaces != null)
                        setFocusedValue(displayValue);
                    onFocus?.(e);
                }}
                onBlur={(e) => {
                    setFocusedValue(null);
                    onBlur?.(e);
                }}
                onChange={(e) => {
                    if (decimalPlaces != null) setFocusedValue(e.target.value);

                    const numeric = Number(e.target.value);
                    if (e.target.value === '') onChange?.(null);
                    else if (!isNaN(numeric))
                        onChange?.(
                            // Bound the value to min/max if they exist
                            Math.min(
                                max ?? Infinity,
                                Math.max(min ?? -Infinity, numeric),
                            ),
                        );
                }}
                ref={ref}
                {...props}
            />
        );
    },
);

NumberInput.displayName = 'NumberInput';

const DataWrapper = React.forwardRef<
    HTMLDivElement,
    React.HTMLProps<HTMLDivElement>
>(({ className, ...props }, ref) => {
    return (
        <div
            className={cn(
                'flex items-center w-full max-w-full rounded-md bg-background-light p-4 text-lg ring-offset-background cursor-pointer hover:bg-background-semilight transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
});

DataWrapper.displayName = 'DataWrapper';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ className, ...props }, ref) => {
        return (
            <textarea
                className={cn(
                    'flex w-full min-h-10 max-h-30 rounded-md bg-background-light hover:bg-background-semilight px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    className,
                )}
                ref={ref}
                {...props}
            />
        );
    },
);

Textarea.displayName = 'Textarea';

export { Input, Textarea, DataWrapper, NumberInput };
